import { useMemo } from "react";
import {
  FaBuilding,
  FaBroadcastTower,
  FaInfoCircle,
  FaMobileAlt,
  FaCloudSunRain,
} from "react-icons/fa";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InstallationMetricSnapshot from "./installation/InstallationMetricSnapshot";
import InstallationStateSupportSection from "./installation/InstallationStateSupportSection";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import {
  formatNetworkOperatorLabel,
  getNetworkQualityLabel,
  isBlankNetworkField,
} from "./installation/installationConfigUtils";
import { useInstallationCamera } from "./installation/useInstallationCamera";

const NETWORK_RECOMMENDED_QUALITY = "fair";
const NETWORK_RECOMMENDED_MARKER_PERCENT = 52;

function getNetworkQualityValue(quality) {
  if (isBlankNetworkField(quality)) {
    return "";
  }
  return String(quality).toLowerCase();
}

function getNetworkVisual(quality) {
  const value = getNetworkQualityValue(quality);

  if (value === "excellent") {
    return { tone: "ok", fillPercent: 100, markerPercent: 100 };
  }
  if (value === "good") {
    return { tone: "ok", fillPercent: 78, markerPercent: 78 };
  }
  if (value === "fair") {
    return { tone: "ok", fillPercent: 52, markerPercent: 52 };
  }
  if (value === "poor") {
    return { tone: "low", fillPercent: 24, markerPercent: 24 };
  }
  return { tone: "neutral", fillPercent: 0, markerPercent: 0 };
}

const AppInstallationNetworkDetail = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const {
    loading,
    networkDetails: loadedNetwork,
    simProvider: loadedProvider,
  } = useInstallationCamera(id);

  const networkDetails = location.state?.networkDetails ?? loadedNetwork;
  const simProvider = location.state?.simProvider ?? loadedProvider;

  const qualityLabel = getNetworkQualityLabel(
    t,
    networkDetails?.networkQuality,
  );
  const operatorLabel = formatNetworkOperatorLabel(t, networkDetails?.operator);
  const qualityValue = getNetworkQualityValue(networkDetails?.networkQuality);
  const hasQuality = Boolean(qualityValue);

  const networkStatusKey = useMemo(() => {
    if (!hasQuality) {
      return "installation_state_network_status_unknown";
    }
    if (
      qualityValue === "excellent" ||
      qualityValue === "good" ||
      qualityValue === "fair"
    ) {
      return "installation_state_network_status_ok";
    }
    return "installation_state_network_status_poor";
  }, [hasQuality, qualityValue]);

  const visual = getNetworkVisual(networkDetails?.networkQuality);

  const isMultiOperator =
    simProvider === "cellhire" || simProvider === "matooma";
  const providerLabel =
    simProvider === "cellhire"
      ? "Cellhire"
      : simProvider === "matooma"
        ? "Matooma"
        : null;

  const guideItems = [
    {
      icon: FaInfoCircle,
      titleKey: "installation_state_network_about_title",
      bodyKey: "installation_state_network_about_body",
    },
    {
      icon: FaMobileAlt,
      titleKey: "installation_state_network_phone_bars_title",
      bodyKey: "installation_state_network_phone_bars_body",
    },
    {
      icon: FaBuilding,
      titleKey: "installation_state_network_indoor_title",
      bodyKey: "installation_state_network_indoor_body",
    },
    {
      icon: FaCloudSunRain,
      titleKey: "installation_state_network_factors_title",
      bodyKey: "installation_state_network_factors_body",
    },
  ];

  return (
    <InstallationSubpageLayout
      pageTitle={t("installation_state_network_page_title")}
      onBack={() => history.goBack()}
      setCookies={setCookies}
      saveUser={saveUser}
    >
      <section className="installation-subpage-card installation-cellular-intro-card">
        <p className="installation-subpage-card-text" role="status">
          {t("installation_state_network_page_intro")}
        </p>
      </section>

      <InstallationMetricSnapshot
        icon={FaBroadcastTower}
        iconVariant="network"
        eyebrow={t("installation_state_network_current_title")}
        value={qualityLabel}
        valueLoading={loading}
        subtitle={
          loading
            ? undefined
            : `${t("installation_state_network_operator_caption")} · ${operatorLabel}`
        }
        hint={!loading ? t(networkStatusKey) : undefined}
        tone={visual.tone}
      />

      <section
        className={`installation-battery-target installation-battery-target-${visual.tone}`}
      >
        <div className="installation-battery-target-header">
          <span className="installation-battery-target-label">
            {t("installation_state_network_threshold_title")}
          </span>
          <span className="installation-battery-target-value">
            {t(`installation_network_quality_${NETWORK_RECOMMENDED_QUALITY}`)}
          </span>
        </div>
        <div className="installation-battery-target-track" aria-hidden>
          <div
            className="installation-battery-target-fill"
            style={{ width: hasQuality ? `${visual.fillPercent}%` : "0%" }}
          />
          <span
            className="installation-battery-target-marker"
            style={{ left: `${NETWORK_RECOMMENDED_MARKER_PERCENT}%` }}
          />
        </div>
        <p className="installation-battery-target-hint">
          {t("installation_state_network_threshold_body")}
        </p>
      </section>

      {isMultiOperator && providerLabel && (
        <section className="installation-subpage-card installation-network-multi-card">
          <span className="installation-subpage-highlight-badge">
            {t("installation_state_network_multi_badge", {
              provider: providerLabel,
            })}
          </span>
          <p className="installation-subpage-card-text">
            {t("installation_state_network_multi_body")}
          </p>
          <ul
            className="installation-subpage-operator-chips"
            aria-label={t("installation_state_network_operators_list")}
          >
            <li>Orange</li>
            <li>Bouygues</li>
            <li>SFR</li>
            <li>Free</li>
          </ul>
          <p className="installation-subpage-highlight-footnote">
            {t("installation_state_network_multi_rescan")}
          </p>
        </section>
      )}

      <section className="installation-battery-guide">
        {guideItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <article
              key={item.titleKey}
              className={
                index > 0
                  ? "installation-battery-guide-item installation-battery-guide-item-divided"
                  : "installation-battery-guide-item"
              }
            >
              <span className="installation-battery-guide-icon" aria-hidden>
                <Icon />
              </span>
              <div className="installation-battery-guide-body">
                <h2 className="installation-battery-guide-title">
                  {t(item.titleKey)}
                </h2>
                <p className="installation-battery-guide-text">
                  {t(item.bodyKey)}
                </p>
              </div>
            </article>
          );
        })}
      </section>

      <section className="installation-subpage-card installation-network-solutions-card">
        <span className="installation-battery-guide-icon installation-network-solutions-icon">
          <FaBroadcastTower aria-hidden />
        </span>
        <h2 className="installation-subpage-card-title">
          {t("installation_state_network_solutions_title")}
        </h2>
        <p className="installation-subpage-card-text">
          {t("installation_state_network_solutions_body")}
        </p>
      </section>

      <InstallationStateSupportSection
        cameraId={id}
        emailSubjectKey="installation_state_network_support_email_subject"
      />
    </InstallationSubpageLayout>
  );
};

export default AppInstallationNetworkDetail;
